import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Form,
  Row,
  Col,
  Space,
  App,
  Select,
  InputNumber,
  Checkbox,
  DatePicker,
} from "antd";

import dayjs from "../../../config/dayjs";
import {
  citizenshipType,
  citizenshipTypeLabels,
  departmentType,
  departmentTypeLabels,
} from "../../../constant/enum";
import {
  getUserById,
  getUsersByBranchId,
} from "../../../pages/user/api/userAPI";
import { getUser } from "../../../services/auth";
import { createPayslip, editPayslip } from "../api/payslipAPI";
import {
  IPayslipRequest,
  IPayslipResponse,
  PayslipRequestSchema,
} from "../type/payslip";

interface PayslipFormProps {
  initialData?: IPayslipRequest | Partial<IPayslipResponse>;
  mode?: "create" | "update";
  payslipId?: string;
}

export const PayslipForm = ({
  initialData,
  mode = "create",
  payslipId,
}: PayslipFormProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message: messageApi } = App.useApp();
  const user = getUser();
  const branchId = user.branch.id;

  const [isSubmitting, setIsSubmitting] = useState(false);

  /* ================ User Data ================ */
  const [searchUserId, setSearchUserId] = useState("");
  const initialUserId = initialData?.userId;

  const { data: userData, isFetching: isUserLoading } = useQuery({
    queryKey: ["users", searchUserId],
    queryFn: () =>
      getUsersByBranchId(branchId!, {
        ...(searchUserId && { name: searchUserId }),
        limit: 100,
        page: 1,
      }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Get initial user data if it exists (only in update mode and if user data is not already provided)
  const initialDataWithUser = initialData as Partial<IPayslipResponse>;
  const shouldFetchInitialUser =
    !!initialUserId && mode === "update" && !initialDataWithUser?.user;
  const { data: initialUserData } = useQuery({
    queryKey: ["user", initialUserId],
    queryFn: () => getUserById(initialUserId!),
    enabled: shouldFetchInitialUser,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Use initialData.user if available (from payslip data), otherwise use fetched initialUserData
  const initialUser = initialDataWithUser?.user || initialUserData;

  const userOptions = [
    ...(userData?.results.map(
      (user: { id: string; name: string; employeeNumber?: string }) => ({
        value: user.id,
        label: `${user.employeeNumber ?? ""} ${user.name}`,
      })
    ) || []),
    // Include initial user if it exists and not in current results
    ...(initialUser && !userData?.results.some((u) => u.id === initialUser.id)
      ? [
          {
            value: initialUserId,
            label: `${initialUser.employeeNumber ?? ""} ${initialUser.name}`,
          },
        ]
      : []),
  ];
  /* ================ End User Data ================ */

  const departmentOptions = departmentType.map((department) => ({
    value: department,
    label: departmentTypeLabels[department],
  }));

  const citizenshipOptions = citizenshipType.map((citizenship) => ({
    value: citizenship,
    label: citizenshipTypeLabels[citizenship],
  }));

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<IPayslipRequest>({
    resolver: zodResolver(PayslipRequestSchema),
    defaultValues: {
      ...initialData,
      isUnder60: initialData?.isUnder60 ?? true,
      payslipDate: initialData?.payslipDate
        ? dayjs(initialData.payslipDate).toDate()
        : dayjs().toDate(),
    },
  });

  const onSubmit = async (data: IPayslipRequest) => {
    setIsSubmitting(true);
    try {
      const submitData = { ...data };

      if (mode === "create") {
        await createPayslip(submitData);
        messageApi.success("Payslip application submitted successfully!");
      } else if (payslipId) {
        await editPayslip(payslipId, submitData);
        messageApi.success("Payslip application updated successfully!");

        // Refresh data
        queryClient.invalidateQueries({ queryKey: ["payslip", payslipId] });
      }
      navigate(-1);
    } catch (error: any) {
      messageApi.error(error.message || "An error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <Form
      layout="vertical"
      onFinish={handleSubmit(onSubmit, () => {
        messageApi.error("Please check all required fields");
      })}
    >
      <Row gutter={[24, 16]}>
        {/* ================ Payslip Date Field ================ */}
        <Col xs={24} md={12}>
          <Form.Item
            label="Month"
            validateStatus={errors.payslipDate ? "error" : ""}
            help={errors.payslipDate?.message}
            required
          >
            <Controller
              name="payslipDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  value={field.value ? dayjs(field.value) : undefined}
                  onChange={(date) => {
                    field.onChange(date ? date.toDate() : undefined);
                  }}
                  picker="month"
                  size="large"
                  style={{
                    width: "100%",
                    borderColor: errors.payslipDate ? "#ff4d4f" : "#a6a6a6",
                  }}
                  format="MMMM YYYY"
                  placeholder="Select Month"
                  disabled={mode === "update"}
                />
              )}
            />
          </Form.Item>
        </Col>

        {/* ================ Department Field ================ */}
        <Col xs={24} md={12}>
          <Form.Item
            label="Department"
            validateStatus={errors.department ? "error" : ""}
            help={errors.department?.message}
            style={{ marginBottom: 24 }}
            required
          >
            <Controller
              name="department"
              control={control}
              render={({ field }) => (
                <Select
                  placeholder="Select Department"
                  {...field}
                  size="large"
                  onChange={(value) => {
                    field.onChange(value);
                  }}
                  options={departmentOptions}
                  allowClear
                />
              )}
            />
          </Form.Item>
        </Col>

        {/* ================ Citizenship Field ================ */}
        <Col xs={24} md={12}>
          <Form.Item
            label="Citizenship"
            validateStatus={errors.citizenship ? "error" : ""}
            help={errors.citizenship?.message}
            style={{ marginBottom: 24 }}
            required
          >
            <Controller
              name="citizenship"
              control={control}
              render={({ field }) => (
                <Select
                  placeholder="Select Citizenship"
                  {...field}
                  size="large"
                  onChange={(value) => {
                    field.onChange(value);
                  }}
                  defaultValue="malaysian"
                  options={citizenshipOptions}
                  allowClear
                />
              )}
            />
          </Form.Item>
        </Col>

        {/* ================ User Field ================ */}
        <Col xs={24} md={12}>
          <Form.Item
            label="User"
            name="userId"
            hasFeedback
            validateStatus={errors.userId ? "error" : ""}
            help={errors.userId?.message}
            required
          >
            <Controller
              name="userId"
              control={control}
              defaultValue={initialUserId}
              render={({ field }) => (
                <Select
                  placeholder="Select User"
                  {...field}
                  size="large"
                  showSearch
                  filterOption={false}
                  loading={isUserLoading}
                  onSearch={(value) => {
                    setSearchUserId(value);
                  }}
                  options={userOptions}
                  allowClear
                  disabled={mode === "update"}
                  notFoundContent={
                    isUserLoading
                      ? "Loading..."
                      : searchUserId
                      ? "No users found"
                      : userOptions.length === 0
                      ? "No users available"
                      : null
                  }
                />
              )}
            />
          </Form.Item>
        </Col>

        {/* ================ Basic Salary Field ================ */}
        <Col xs={24} md={12}>
          <Form.Item
            label="Basic Salary"
            name="basicSalary"
            validateStatus={errors.basicSalary ? "error" : ""}
            help={errors.basicSalary?.message}
          >
            <Controller
              name="basicSalary"
              control={control}
              render={({ field }) => (
                <InputNumber
                  min={0}
                  step={0.01}
                  placeholder="Enter amount"
                  {...field}
                  onChange={(value) => field.onChange(value ?? undefined)}
                  size="large"
                  style={{
                    width: "100%",
                    borderColor: errors.basicSalary ? "#ff4d4f" : "#A6A6A6",
                  }}
                  formatter={(value) =>
                    value
                      ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      : ""
                  }
                />
              )}
            />
          </Form.Item>
        </Col>

        {/* ================ Fixed Allowance Field ================ */}
        <Col xs={24} md={12}>
          <Form.Item
            label="Fixed Allowance"
            name="fixedAllowance"
            validateStatus={errors.fixedAllowance ? "error" : ""}
            help={errors.fixedAllowance?.message}
          >
            <Controller
              name="fixedAllowance"
              control={control}
              render={({ field }) => (
                <InputNumber
                  min={0}
                  step={0.01}
                  placeholder="Enter amount"
                  {...field}
                  onChange={(value) => field.onChange(value ?? undefined)}
                  size="large"
                  style={{
                    width: "100%",
                    borderColor: errors.fixedAllowance ? "#ff4d4f" : "#A6A6A6",
                  }}
                  formatter={(value) =>
                    value
                      ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      : ""
                  }
                />
              )}
            />
          </Form.Item>
        </Col>

        {/* ================ Staff Incentive Field ================ */}
        <Col xs={24} md={12}>
          <Form.Item
            label="Staff Incentive"
            name="staffIncentive"
            validateStatus={errors.staffIncentive ? "error" : ""}
            help={errors.staffIncentive?.message}
          >
            <Controller
              name="staffIncentive"
              control={control}
              render={({ field }) => (
                <InputNumber
                  min={0}
                  step={0.01}
                  placeholder="Enter amount"
                  {...field}
                  onChange={(value) => field.onChange(value ?? undefined)}
                  size="large"
                  style={{
                    width: "100%",
                    borderColor: errors.staffIncentive ? "#ff4d4f" : "#A6A6A6",
                  }}
                  formatter={(value) =>
                    value
                      ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      : ""
                  }
                />
              )}
            />
          </Form.Item>
        </Col>

        {/* ================ Other Incentive Field ================ */}
        <Col xs={24} md={12}>
          <Form.Item
            label="Other Incentive"
            name="otherIncentive"
            validateStatus={errors.otherIncentive ? "error" : ""}
            help={errors.otherIncentive?.message}
          >
            <Controller
              name="otherIncentive"
              control={control}
              render={({ field }) => (
                <InputNumber
                  min={0}
                  step={0.01}
                  placeholder="Enter amount"
                  {...field}
                  onChange={(value) => field.onChange(value ?? undefined)}
                  size="large"
                  style={{
                    width: "100%",
                    borderColor: errors.otherIncentive ? "#ff4d4f" : "#A6A6A6",
                  }}
                  formatter={(value) =>
                    value
                      ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      : ""
                  }
                />
              )}
            />
          </Form.Item>
        </Col>

        {/* ================ Is Under 60 Field ================ */}
        <Col xs={24} md={12}>
          <Form.Item
            label=" "
            name="isUnder60"
            validateStatus={errors.isUnder60 ? "error" : ""}
            help={errors.isUnder60?.message}
          >
            <Controller
              name="isUnder60"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={field.value || false}
                  onChange={(e) => {
                    field.onChange(e.target.checked);
                  }}
                >
                  Employee is under 60 years old
                </Checkbox>
              )}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* ================ Form Actions ================ */}
      <Form.Item>
        <Row justify="end">
          <Space
            direction="horizontal"
            wrap
            style={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Button
              style={{ margin: 5 }}
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              style={{ margin: 5 }}
              type="primary"
              htmlType="submit"
              loading={isSubmitting}
            >
              {mode === "create" ? "Submit Payslip" : "Update Payslip"}
            </Button>
          </Space>
        </Row>
      </Form.Item>
    </Form>
  );
};
